export interface TenantConfig {
  tenantId: string
  tenantName: string
  isAdmin: boolean
  ssoProviderName?: string
  auth: {
    user_pool_id: string
    aws_region: string
    user_pool_client_id: string
    identity_pool_id: string
    mfa_methods?: string[]
    standard_required_attributes?: string[]
    username_attributes?: string[]
    user_verification_types?: string[]
    mfa_configuration?: string
    password_policy?: {
      min_length: number
      require_lowercase: boolean
      require_numbers: boolean
      require_symbols: boolean
      require_uppercase: boolean
    }
    unauthenticated_identities_enabled?: boolean
    oauth?: {
      domain?: string
      scopes?: string[]
      redirect_sign_in_uri?: string | string[]
      redirect_sign_out_uri?: string | string[]
      redirect_sign_in?: string | string[]
      redirect_sign_out?: string | string[]
      response_type?: string
      identity_providers?: string[]
    }
  }
  data?: {
    url: string
    aws_region: string
    api_key?: string
    default_authorization_type: string
    authorization_types: string[]
    model_introspection?: any
  }
  version?: string
}

export interface TenantInfo {
  id: string
  name: string
  domain?: string
}

class TenantConfigService {
  private static instance: TenantConfigService
  private configCache = new Map<string, TenantConfig>()
  private tenantListCache: TenantInfo[] | null = null
  private currentConfig: TenantConfig | null = null
  private localModelIntrospection: any = null
  private readonly configBaseUrl: string
  private readonly retryAttempts = 3
  private readonly retryDelay = 1000

  constructor() {
    this.configBaseUrl = import.meta.env.VITE_CONFIG_BASE_URL || ''
    
    if (!this.configBaseUrl) {
      console.warn('VITE_CONFIG_BASE_URL not configured. Using fallback tenant resolution.')
    }
  }

  static getInstance(): TenantConfigService {
    if (!TenantConfigService.instance) {
      TenantConfigService.instance = new TenantConfigService()
    }
    return TenantConfigService.instance
  }

  private async loadLocalModelIntrospection(): Promise<void> {
    if (this.localModelIntrospection) {
      return // Already loaded
    }

    try {
      // Import the local amplify_outputs.json to get the model_introspection
      const localConfig = await import('../../amplify_outputs.json')
      this.localModelIntrospection = localConfig.default?.data?.model_introspection
    } catch (error) {
      console.warn('Could not load local amplify_outputs.json for model_introspection:', error)
      this.localModelIntrospection = null
    }
  }

  /**
   * Resolve the domainId (website subdomain) from the environment or hostname.
   * This is NOT the data tenantId (md5) — the canonical tenantId lives inside the
   * fetched config file. The domainId is used to locate that config in CloudFront.
   */
  resolveDomainId(): string {
    // First check environment variable
    const envDomainId = import.meta.env.VITE_DOMAIN_ID
    if (envDomainId) {
      return envDomainId
    }

    // Extract from domain (e.g., example.com -> example)
    const hostname = window.location.hostname

    // Handle localhost and development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'default'
    }

    // Extract subdomain from domain
    const parts = hostname.split('.')
    if (parts.length >= 2) {
      return parts[0]
    }

    return 'default'
  }

  /**
   * Get tenant configuration with caching and retry logic
   */
  async getTenantConfig(domainId?: string): Promise<TenantConfig> {
    const resolvedDomainId = domainId || this.resolveDomainId()

    // Check cache first
    if (this.configCache.has(resolvedDomainId)) {
      return this.configCache.get(resolvedDomainId)!
    }

    // Load local model_introspection if not already loaded
    await this.loadLocalModelIntrospection()

    // If no config URL, use fallback
    if (!this.configBaseUrl) {
      throw new Error(`Failed to load configuration`)
    }

    // Fetch from remote with retry logic
    for (let attempt = 1; attempt <= this.retryAttempts; attempt++) {
      try {
        const config = await this.fetchTenantConfig(resolvedDomainId)
        this.configCache.set(resolvedDomainId, config)
        this.currentConfig = config
        return config
      } catch (error) {
        console.warn(`Attempt ${attempt} failed to fetch tenant config:`, error)
        
        if (attempt === this.retryAttempts) {
          console.error('All retry attempts failed, using fallback config')
          throw new Error(`Failed to load configuration (2): ${error}`)
        }
        
        await this.delay(this.retryDelay * attempt)
      }
    }

    throw new Error('Failed to get tenant configuration')
  }

  /**
   * Fetch tenant configuration from remote
   */
  private async fetchTenantConfig(domainId: string): Promise<TenantConfig> {
    const configUrl = `${this.configBaseUrl}/${domainId}/config.json`
    
    const response = await fetch(configUrl)

    if (!response.ok) {
      throw new Error(`Failed to fetch config: ${response.status} ${response.statusText}`)
    }

    const rawConfig = await response.json()
    
    // Validate required fields
    if (!rawConfig.auth?.user_pool_id) {
      throw new Error('Invalid tenant configuration: missing required auth fields')
    }

    // A data block with no endpoint would point the GraphQL client at nothing and fail on
    // every query instead of at startup. An absent data block is left alone — configs
    // published before it existed fall back to the local outputs downstream.
    if (rawConfig.data && !rawConfig.data.url) {
      throw new Error('Invalid tenant configuration: data.url is empty')
    }

    // Transform the raw config to our interface format and merge model_introspection
    const config: TenantConfig = {
      // The config is located in CloudFront by domainId (the subdomain), but the
      // canonical tenantId lives inside the file. Fall back to the fetch key for
      // legacy configs that don't yet carry a tenantId.
      tenantId: rawConfig.tenantId || domainId,
      tenantName: rawConfig.tenantName || `Tenant ${domainId}`,
      isAdmin: rawConfig.isAdmin || false,
      ssoProviderName: rawConfig.ssoProviderName,
      auth: rawConfig.auth,
      data: rawConfig.data ? {
        ...rawConfig.data,
        // Merge model_introspection from local amplify_outputs.json if available
        model_introspection: this.localModelIntrospection || rawConfig.data.model_introspection
      } : undefined,
      version: rawConfig.version || "1.3"
    }

    return config
  }

  /**
   * Get list of available tenants
   */
  async getAvailableTenants(): Promise<TenantInfo[]> {
    if (this.tenantListCache) {
      return this.tenantListCache
    }

    if (!this.configBaseUrl) {
      // Return current tenant only when no config URL
      const currentDomainId = this.resolveDomainId()
      this.tenantListCache = [{ id: currentDomainId, name: `Tenant ${currentDomainId}` }]
      return this.tenantListCache
    }

    try {
      const response = await fetch(`${this.configBaseUrl}/tenants.json`)
      if (response.ok) {
        this.tenantListCache = await response.json()
        return this.tenantListCache!
      }
    } catch (error) {
      console.warn('Failed to fetch tenant list:', error)
    }

    // Fallback to current tenant
    const currentDomainId = this.resolveDomainId()
    this.tenantListCache = [{ id: currentDomainId, name: `Tenant ${currentDomainId}` }]
    return this.tenantListCache
  }

  /**
   * Get current tenant configuration
   */
  getCurrentConfig(): TenantConfig | null {
    return this.currentConfig
  }

  /**
   * Set current tenant configuration
   */
  setCurrentConfig(config: TenantConfig): void {
    this.currentConfig = config
  }

  /**
   * Method to refresh local model_introspection (call this when schema changes)
   */
  async refreshLocalModelIntrospection(): Promise<void> {
    this.localModelIntrospection = null
    await this.loadLocalModelIntrospection()
    // Clear cache to force reload of tenant configs with new model_introspection
    this.clearCache()
  }

  /**
   * Clear configuration cache
   */
  clearCache(): void {
    this.configCache.clear()
    this.tenantListCache = null
  }

  /**
   * Get current tenant info
   */
  async getCurrentTenantInfo(): Promise<TenantInfo> {
    const domainId = this.resolveDomainId()
    const tenants = await this.getAvailableTenants()

    return tenants.find(t => t.id === domainId) || {
      id: domainId,
      name: `Tenant ${domainId}`
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
}

export const tenantConfigService = TenantConfigService.getInstance()
